import React, { useState } from 'react';
import Cookies from 'js-cookie';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import { Particles } from '../components/Particles.tsx';
import { 
  Button, TextField, Grid, Typography, Paper, Box, IconButton, 
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, Zoom, useTheme 
} from '@mui/material';
import { FileCopy, InfoOutlined } from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';

// --- Stílusok és Animációk ---
const backgroundMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(4px)',
    backgroundColor: darkMode ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.4)',
  },
  '& .MuiPaper-root': {
    borderRadius: '24px',
    border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(33, 203, 243, 0.3)',
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #f0f4ff 30%, #c2e9fb 90%)',
    backgroundSize: '200% 200%',
    animation: `${backgroundMove} 12s ease infinite`,
    color: darkMode ? 'white' : '#1a237e',
    boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
  },
  '& .MuiDialogTitle-root': { 
    fontWeight: 'bold', 
    textAlign: 'center', 
    fontSize: '1.4rem',
    color: darkMode ? 'white' : '#1a237e',
    paddingTop: '24px'
  },
}));

// Véletlenszerű kulcsgeneráló funkció
const generateRandomKey = (length: number, isStrong: boolean = false): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const strongCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_-+=<>?/[]{}|:;';
  const chars = isStrong ? strongCharacters : characters;
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const Encryption: React.FC = () => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const isAuthenticated = !!Cookies.get('token');
  
  const [randomKeys, setRandomKeys] = useState<{ [key: string]: string }>({});
  const [openFields, setOpenFields] = useState<{ [key: string]: boolean }>({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [infoOpen, setInfoOpen] = useState(false); // Dialog állapota

  const handleGenerateKey = (type: string) => {
    let length = 0;
    let isStrong = false;

    switch (type) {
      case 'Basic8': length = 8; break;
      case 'Basic10': length = 10; break;
      case 'Basic16': length = 16; break;
      case 'StrongNotAmbiguous': length = 16; isStrong = true; break;
      case 'Strong': length = 16; isStrong = true; break;
      case 'High': length = 32; isStrong = true; break;
      case 'Encryption256': length = 64; break;
      case 'Wep64': length = 6; break;
      case 'Wep128': length = 13; break;
      case 'Wep256': length = 16; break;
      default: length = 8;
    }

    const key = generateRandomKey(length, isStrong);
    setRandomKeys(prev => ({ ...prev, [type]: key }));
    setOpenFields(prev => ({ ...prev, [type]: true }));
  };

  const handleCopy = (key: string) => {
    navigator.clipboard.writeText(key).then(() => {
      setSnackbarMessage('Key copied to clipboard!');
      setOpenSnackbar(true);
    });
  };

  const handleToggleFields = (action: 'open' | 'collapse') => {
    const types = ['Basic8', 'Basic10', 'Basic16', 'StrongNotAmbiguous', 'Strong', 'High', 'Encryption256', 'Wep64', 'Wep128', 'Wep256'];
    const updatedState = types.reduce((acc, key) => {
      acc[key] = action === 'open';
      return acc;
    }, {} as { [key: string]: boolean });
    setOpenFields(updatedState);
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', paddingTop: '64px' }}>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `}
      </style>
      
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1 }}>
        <Particles />
      </div>
      
      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}

      <Grid container spacing={3} justifyContent="center" style={{ marginTop: '50px', padding: '0 20px' }}>
        <Grid item xs={12} md={8}>
          <Paper 
            elevation={3} 
            sx={{ 
              padding: '30px', 
              borderRadius: '24px',
              background: darkMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)'
            }}
          >
            <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                <Typography variant="h4" align="center" sx={{ color: darkMode ? 'white' : '#1a237e', fontWeight: 'bold' }}>
                    Random Key Generator
                </Typography>
                <IconButton onClick={() => setInfoOpen(true)} sx={{ color: darkMode ? '#ff4081' : '#ff6f00' }}>
                    <InfoOutlined />
                </IconButton>
            </Box>

            <Grid container justifyContent="center" style={{ marginTop: '20px' }}>
              <Button
                variant="outlined"
                onClick={() => handleToggleFields('collapse')}
                sx={{
                  margin: '0 10px',
                  animation: `${pulse} 2s infinite`,
                  borderColor: '#ff6f00',
                  color: '#ff6f00',
                  '&:hover': { backgroundColor: '#ff6f00', color: 'white', borderColor: '#ff6f00' },
                }}
              >
                Collapse All
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleToggleFields('open')}
                sx={{
                  margin: '0 10px',
                  animation: `${pulse} 2s infinite`,
                  borderColor: '#ff4081',
                  color: '#ff4081',
                  '&:hover': { backgroundColor: '#ff4081', color: 'white', borderColor: '#ff4081' },
                }}
              >
                Open All
              </Button>
            </Grid>

            <Grid container spacing={2} justifyContent="center" style={{ marginTop: '30px' }}>
              {[
                { label: 'Basic 8', type: 'Basic8' },
                { label: 'Basic 10', type: 'Basic10' },
                { label: 'Basic 16', type: 'Basic16' },
                { label: 'Strong Not Ambiguous', type: 'StrongNotAmbiguous' },
                { label: 'Strong', type: 'Strong' },
                { label: 'High', type: 'High' },
                { label: 'Encryption Key 256', type: 'Encryption256' },
                { label: '64 WEP', type: 'Wep64' },
                { label: '128 WEP', type: 'Wep128' },
                { label: '256 WEP', type: 'Wep256' }
              ].map(({ label, type }) => (
                <Grid item xs={12} md={6} key={type}>
                  <Button 
                    fullWidth 
                    variant="contained" 
                    onClick={() => handleGenerateKey(type)}
                    sx={{
                      background: 'linear-gradient(45deg, #ff6f00, #ff4081)',
                      color: 'white',
                      fontWeight: 'bold',
                      borderRadius: '12px',
                      transition: '0.3s ease-out',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #ff4081, #ff6f00)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 6px 20px rgba(255, 64, 129, 0.4)',
                      },
                    }}
                  >
                    {label}
                  </Button>

                  {openFields[type] && randomKeys[type] && (
                    <Box sx={{ marginTop: '15px', position: 'relative' }}>
                      <TextField
                        fullWidth
                        value={randomKeys[type]}
                        InputProps={{ readOnly: true }}
                        label={`Generated ${label}`}
                        variant="filled"
                        sx={{ 
                            marginBottom: '10px',
                            '& .MuiFilledInput-root': {
                                borderRadius: '8px',
                                backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
                            }
                        }}
                      />
                      <IconButton 
                        onClick={() => handleCopy(randomKeys[type])} 
                        sx={{ position: 'absolute', right: '10px', top: '10px', color: '#ff4081' }}
                      >
                        <FileCopy />
                      </IconButton>
                    </Box>
                  )}
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* --- Animált Dialog --- */}
      <AnimatedDialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        darkMode={darkMode}
        TransitionComponent={Zoom}
      >
        <DialogTitle>Security Information</DialogTitle>
        <DialogContent>
            <Typography variant="body1" sx={{ textAlign: 'center', lineHeight: 1.6 }}>
                These keys are generated locally in your browser using secure random functions. 
                Strong keys include special characters and are recommended for high-security applications 
                like WPA2 or database encryption.
            </Typography>
            <Button 
                fullWidth 
                onClick={() => setInfoOpen(false)} 
                sx={{ mt: 3, color: darkMode ? '#ff4081' : '#1a237e', fontWeight: 'bold' }}
            >
                Understood
            </Button>
        </DialogContent>
      </AnimatedDialog>

      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)}>
        <Alert severity="success" sx={{ width: '100%', borderRadius: '12px' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Encryption;